/**
 * @file
 *
 * This file describes the 'Reducer' mapping, is also a good point to define 'Epic' functions.
 * Note that an Action always run first through the reducer before the Epic even receive it.
 * @author JPablo
 */
import {AnyAction} from "redux";
import "rxjs/operators/delay";
import {authenticatedAjax, REQUEST} from "../../../config/ajax-wrapper";
import {
    CONSULTAR_REPORTE,
    ERROR_CONSULTANDO_REPORTE,
    ERROR_OBTENIENDO_XLS,
    errorConsultandoReporte, errorObteniendoXLS,
    REPORTE_CONSULTADO,
    reporteConsultado,
    XLS_OBTENIDO,
    xlsObtenido, 
    CONSULTAR_HISTORICO,
    OBTENER_XLS_REPORTE,
    OBTENER_XLS_HISTORICO,
    HISTORICO_CONSULTADO,
    ERROR_CONSULTANDO_HISTORICO,
    historicoConsultado
} from "./reportesActions";
import {FiltroReporte} from "../modelo/Reporte";
import {Documento, TrazaEstado} from "../../documentos/modelo/Documento";
import {DATE_FORMAT_WITH_HOUR} from "../../general/componentes/DatePicker";
import {obtenerTipoCliente} from "../../documentos/modelo/TiposCliente";
import * as moment from "moment";
import fileDownload from "js-file-download";
import { FiltroHistoricos } from "../modelo/Historicos";

/*
* Epics
* https://redux-observable.js.org/docs/basics/Epics.html
*/
export const consultarReporteEpic = action$ =>
    action$.ofType(CONSULTAR_REPORTE)
        .mergeMap((action) => {
            const url = `reporte?${obtenerFiltro(action.filtro)}`;

            return authenticatedAjax
                .get(
                    url
                )
                .map((response) =>{
                    const respuesta = JSON.parse(response.response);
                    return reporteConsultado(parseDocumentos(respuesta.respuesta),respuesta.paginas);
                }  
                )
                .catch(response =>
                    [errorConsultandoReporte()]
                );
        });

export const consultarHistoricoEpic = action$ =>
    action$.ofType(CONSULTAR_HISTORICO)
        .mergeMap((action) => {
            const url = `reporte/historico?${obtenerFiltroHistorico(action.filtro)}`;

            return authenticatedAjax
                .get(
                    url
                )
                .map((response) => {
                    const respuesta = JSON.parse(response.response);
                    return historicoConsultado(parseHistorico(respuesta.respuesta),respuesta.paginas);
                })
                .catch(response =>
                    [errorConsultandoReporte()]
                );
        });

export const obtenerXLSEpic = action$ =>
    action$.ofType(OBTENER_XLS_REPORTE)
        .mergeMap((action) => {

            const url = `reporte/xls?${obtenerFiltro(action.filtro)}`;

            return authenticatedAjax
                .getBlob(
                    url
                )
                .map((response) => {
                        const fileName = `reporte_${moment(moment.now()).format("DDMMYYYY_HH:mm:ss")}.xlsx`;

                        fileDownload(
                            response.response,
                            fileName,
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        );

                        return xlsObtenido();
                    }
                )
                .catch(response =>
                    [errorObteniendoXLS()]
                );
        });

export const obtenerHistoricoXLSEpic = action$ =>
    action$.ofType(OBTENER_XLS_HISTORICO)
        .mergeMap((action) => {

            const url = `reporte-historico/xls?${obtenerFiltroHistorico(action.filtro)}`;

            return authenticatedAjax
                .getBlob(
                    url
                )
                .map((response) => {
                        const fileName = `reporte_${moment(moment.now()).format("DDMMYYYY_HH:mm:ss")}.xlsx`;

                        fileDownload(
                            response.response,
                            fileName,
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        );

                        return xlsObtenido();
                    }
                )
                .catch(response =>
                    [errorObteniendoXLS()]
                );
        });

/*
* Reducer
* https://redux.js.org/docs/basics/Reducers.html
 */
export const reportesReducer = (state = {
    estado: REQUEST.NONE,
    estadoXLS: REQUEST.NONE,
    documentos: [], 
    estados: [],
    tamanoPagina: 10,
    paginaActual: 0
}, action: AnyAction) => {
    switch (action.type) {
        case CONSULTAR_REPORTE:
            return {...state, 
                estado: REQUEST.PENDING, 
                documentos: [], estadoXLS: REQUEST.NONE,
                paginaActual: action.filtro.pagina,
                tamanoPagina: action.filtro.registrosPorPagina
            };
        case REPORTE_CONSULTADO:
            return {
                ...state, 
                estado: REQUEST.SUCCESS, 
                documentos: action.documentos,
                paginas: action.paginas
            };
        case ERROR_CONSULTANDO_REPORTE:
            return {...state, estado: REQUEST.ERROR, documentos: []};
        case OBTENER_XLS_REPORTE:
            return {...state, estadoXLS: REQUEST.PENDING};
        case CONSULTAR_HISTORICO:
            return {
                ...state,
                estado: REQUEST.PENDING,
                estados: [],
                paginaActual: action.filtro.pagina,
                tamanoPagina: action.filtro.registrosPorPagina,
                estadoXLS: REQUEST.NONE
            };
        case HISTORICO_CONSULTADO:
            return {...state, 
                estado: REQUEST.SUCCESS, 
                estados: action.estados, 
                paginas: action.paginas
            };
        case ERROR_CONSULTANDO_HISTORICO:
            return {...state, estado: REQUEST.ERROR, estados: []};
        case OBTENER_XLS_HISTORICO:
            return {...state, estadoXLS: REQUEST.PENDING};
        case XLS_OBTENIDO:
            return {...state, estadoXLS: REQUEST.SUCCESS};
        case ERROR_OBTENIENDO_XLS:
            return {...state, estadoXLS: REQUEST.ERROR};
        default:
            return state;
    }
};

// Parser functions
const parseDocumentos = (response: Array<any>): Array<Documento> => {
    const documentos = response.map(d => {
        return {
            afiliado: d.afiliado,
            estado: d.estado,
            fechaRadicacion: moment(d.fechaRadicacion).format(DATE_FORMAT_WITH_HOUR),
            documentos: d.documentos,
            tipoCliente: obtenerTipoCliente(d.tipoAfiliado),
            fechaAsignacion: moment(d.fechaAsignacion).format(DATE_FORMAT_WITH_HOUR),
            tipoTramite: d.tipoTramite,
            usuarioAprueba: d.usuarioAprueba,
            poliza: d.poliza ? d.poliza : "",
            fechaInicioCobertura:   d.fechaInicioCobertura ? d.fechaInicioCobertura : "",
            dniEmpleador: d.dniEmpleador ? d.dniEmpleador : "",
            observacion: d.observacion ? d.observacion : "",
            id: d.id
        };
    });
    return documentos;
};

const parseHistorico = (response: Array<any>): Array<TrazaEstado> => {
    const traza = response.map(
        info => {
            return {
                dni : info.dni,
                tipoCliente : obtenerTipoCliente(info.tipoCliente),
                dniAprueba: info.dniUsuarioOperacion,
                oficinaRecibe: info.nombreOficina,
                estado: info.estado,
                fechaModificacion: info.fechaCambio,
                tipoTramite: info.tipoTramite,
                nombreAfiliado: info.nombreAfiliado ? info.nombreAfiliado : "",
                dniEmpleador: info.dniEmpleador ? info.dniEmpleador : ""
            };
        }
    );
    return traza;
};

const obtenerFiltro = (filtro: FiltroReporte) => {
    var filter = "";

    if (filtro) {

        filter = filtro.oficina ? `oficina=${filtro.oficina}` : "";

        filter += filtro && filtro.dni ?
        (filter ? `&dniEmpleador=${filtro.dni}` :
                `dniEmpleador=${filtro.dni}`)
            : ""; 

        filter += filtro.tipoCliente ?
            (filter ? `&tipoCliente=${filtro.tipoCliente}` : `tipoCliente=${filtro.tipoCliente}`)
            : "";

        filter += filtro.estado ?
            (filter ? `&estado=${filtro.estado}` : `estado=${filtro.estado}`)
            : "";

        filter += filtro.fechaInicio ?
            (filter ? `&fechaInicio=${filtro.fechaInicio}` : `fechaInicio=${filtro.fechaInicio}`)
            : "";

        filter += filtro.fechaFin ?
            (filter ? `&fechaFin=${filtro.fechaFin}` : `fechaInicio=${filtro.fechaFin}`)
            : "";

        filter += filtro.fechaInicioAprobacion ?
            (filter ? `&fechaInicioAprobacion=${filtro.fechaInicioAprobacion}` : `fechaInicioAprobacion=${filtro.fechaInicioAprobacion}`)
            : "";

        filter += filtro.fechaFinAprobacion ?
            (filter ? `&fechaFinAprobacion=${filtro.fechaFinAprobacion}` : `fechaInicio=${filtro.fechaFinAprobacion}`)
            : "";

        filter += filtro.tipoTramite ?
            (filter ? `&tipoTramite=${filtro.tipoTramite}` : `tipoTramite=${filtro.tipoTramite}`)
            : "";

        filter += filtro.registrosPorPagina ?
            (filter ? `&registrosPorPagina=${filtro.registrosPorPagina}` :  `registrosPorPagina=${filtro.registrosPorPagina}`)
            : "";
    
        filter += filtro.pagina ?
            (filter ? `&pagina=${(filtro.pagina + 1)}` :
                `pagina=${(filtro.pagina + 1)}`)
            : "";
    }

    return filter;
};

const obtenerFiltroHistorico = (filtro: FiltroHistoricos) => {
    var filter = filtro && filtro.dni ? 
            `dni=${filtro.dni}` 
        : "";
    
    filter += filtro && filtro.dniEmpleador ?
        (filter ? `&dniEmpleador=${filtro.dniEmpleador}` :
                `dniEmpleador=${filtro.dniEmpleador}`)
            : ""; 

    filter += filtro && filtro.fechaInicio ?
    (filter ? `&fechaInicio=${filtro.fechaInicio}` :
            `fechaInicio=${filtro.fechaInicio}`)
        : ""; 

    filter += filtro && filtro.fechaFin ?
        (filter ? `&fechaFin=${filtro.fechaFin}` :
                `fechaFin=${filtro.fechaFin}`)
            : ""; 

    filter += filtro && filtro.tipoCliente ? 
        (filter ? `&tipoCliente=${filtro.tipoCliente}` :
                `tipoCliente=${filtro.tipoCliente}`)
            : "";
    
    filter += filtro.registrosPorPagina ?
        (filter ? `&registrosPorPagina=${filtro.registrosPorPagina}` :
            `registrosPorPagina=${filtro.registrosPorPagina}`)
        : "";

    filter += filtro.pagina ?
        (filter ? `&pagina=${(filtro.pagina + 1)}` :
            `pagina=${(filtro.pagina + 1)}`)
        : "";

    return filter;

};


// WEBPACK FOOTER //
// ./src/co/sura/dominio/reportes/modulos/reportesReducers.ts